/**
 * ClustersPage — the Universe overview page (bd deepcontext-d68.6).
 *
 * Composes the D3 bubble graph and the anchor-search bar. Reads
 * `GET /api/clusters?market=<market>` for the bubble data and posts to
 * `/api/clusters/score` when an anchor is set.
 *
 * bd deepcontext-r4xu: the right-rail topic-detail panel is gone —
 * `ClustersGraph` now renders it as an in-canvas overlay anchored to the
 * clicked bubble, so this page no longer tracks a "selected" cluster at all.
 * The Card/CardContent wrapper around the graph is gone too — the graph's
 * own container is the single bordered chart box, spanning full width. The
 * market selector + URL-count pill move from the ClustersTopBar portal into
 * this page's own controls row, alongside the AnchorBar (see
 * `ClustersTopBar.tsx` for why: the portal slot can't flex-grow).
 *
 * Market selection (bd-d68.14): the active market comes from
 * `ClustersMarketContext` (provider mounted at `pages/Clusters/index.tsx`).
 * Tests may pass an explicit `market` prop to bypass the context — in that
 * mode `MarketSelector`/`UrlCountBadge` are skipped (both require a real
 * provider), leaving just the anchor bar.
 */

import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import AnchorBar, {AnchorBlueprintOption} from './AnchorBar';
import ClustersGraph from './ClustersGraph';
import MarketSelector from './components/MarketSelector';
import UrlCountBadge from './components/UrlCountBadge';
import ClustersMarketContext from './contexts/ClustersMarketContext';
import {useChartPalette} from '../../components/charts/useChartMode';
import {getMapPanelStyle} from '../../components/charts/mapPanelStyle';
import {
    isAbortError,
    extractApiError,
    clustersApi,
    ClustersPayload,
    StoryFitScore,
} from '../../api/clusters';
import {promptApi} from '../../services/api';

function fitMapFromScores(scores: StoryFitScore[]): Map<string, number> {
    const map = new Map<string, number>();
    for (const s of scores) {
        map.set(String(s.story_id), s.fit_score);
    }
    return map;
}

interface ClustersPageProps {
    /**
     * Override the context-provided market. Production code leaves this
     * undefined and reads `selectedMarket` from `ClustersMarketContext`; tests
     * pass an explicit code to avoid having to mount the provider.
     */
    market?: string;
}

const ClustersPage: React.FC<ClustersPageProps> = ({market: marketProp}) => {
    // Read the context tolerantly so tests that render this page without a
    // provider (passing `market` as a prop) still mount cleanly.
    const ctx = useContext(ClustersMarketContext);
    const palette = useChartPalette();
    const market = marketProp ?? ctx?.selectedMarket ?? null;
    // Human-readable label for the active country market, surfaced in the
    // anchor result so users see that fit is scored against the selected
    // country market (markets are keyed by bid-request country).
    const marketLabel =
        ctx?.markets.find((m) => m.code === market)?.label ?? market ?? undefined;
    // Publish the total URL count into context so the sibling top bar can
    // render it (the bar is mounted by the parent shell, not by this page).
    const setTotalUrlCount = ctx?.setTotalUrlCount;
    const setUnsortedUrlCount = ctx?.setUnsortedUrlCount;

    // bd deepcontext-d68.17: when the user clicks "Open in Universe" on a
    // saved brief, BriefsPage navigates here with `location.state.briefText`.
    // Read it once on mount, then clear it via `navigate('.', {replace: true})`
    // so a page refresh doesn't re-seed the anchor.
    const location = useLocation();
    const navigate = useNavigate();
    const seedFromStateRef = useRef<{
        briefId?: string;
        briefName?: string;
        briefText: string;
    } | null>(null);
    if (seedFromStateRef.current === null) {
        const s = location.state as
            | {briefId?: string; briefName?: string; briefText?: string}
            | null
            | undefined;
        if (s && typeof s.briefText === 'string' && s.briefText.trim()) {
            seedFromStateRef.current = {
                briefId: s.briefId,
                briefName: s.briefName,
                briefText: s.briefText,
            };
        }
    }
    const [payload, setPayload] = useState<ClustersPayload | null>(null);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);

    const [anchor, setAnchor] = useState<string>('');
    // deepcontext-iasew: Brand Blueprints available to anchor to, and the
    // currently-anchored blueprint id (null when the anchor is free-text).
    const [blueprints, setBlueprints] = useState<AnchorBlueprintOption[]>([]);
    // deepcontext-vpbwl: track the blueprint fetch so the AnchorBar can render
    // the Blueprint/Free-brief toggle IMMEDIATELY on mount (while the fetch is
    // still in flight) instead of popping it in only after the data resolves.
    const [blueprintsLoading, setBlueprintsLoading] = useState(true);
    const [selectedBlueprintId, setSelectedBlueprintId] = useState<string | null>(null);
    const [scoring, setScoring] = useState(false);
    const [scoreError, setScoreError] = useState<string | null>(null);
    const [fitMap, setFitMap] = useState<Map<string, number> | null>(null);
    const [fitStats, setFitStats] = useState<{
        segmentCount: number;
        avgFit: number;
    } | null>(null);

    // Cancel an in-flight scoring request when the user types a new brief
    // before the previous result lands, OR when this page unmounts mid-flight
    // (bd deepcontext-26s).
    const scoreAbortRef = useRef<AbortController | null>(null);

    // Load the Universe payload for the active market.
    useEffect(() => {
        if (!market) {
            // Provider still resolving its default — stay in the loading state
            // until a market becomes available.
            setLoading(true);
            return;
        }
        const controller = new AbortController();
        setLoading(true);
        setLoadError(null);
        clustersApi
            .getClusters(market, {signal: controller.signal})
            .then((data) => {
                if (controller.signal.aborted) return;
                setPayload(data);
                // Total pages across every visible story (top + ranked rest);
                // "unsorted" repurposes `orphan_count` — pages that never
                // formed a story (spec section 10) — as the closest analog to
                // the old genre pipeline's "clustered but not shown" count.
                const totalPages = [...data.top, ...data.rest].reduce(
                    (sum, s) => sum + s.member_count,
                    0,
                );
                setTotalUrlCount?.(totalPages);
                setUnsortedUrlCount?.(data.orphan_count);
            })
            .catch((err: unknown) => {
                if (isAbortError(err) || controller.signal.aborted) return;
                setLoadError(extractApiError(err, 'Failed to load Universe.'));
                setTotalUrlCount?.(null);
                setUnsortedUrlCount?.(null);
            })
            .finally(() => {
                if (!controller.signal.aborted) setLoading(false);
            });
        return () => {
            controller.abort();
        };
    }, [market, setTotalUrlCount, setUnsortedUrlCount]);

    // deepcontext-iasew: load the user's Brand Blueprints once so the AnchorBar
    // can offer them as an anchor source. Reuses the existing `/api/prompts`
    // list endpoint (no new endpoint). Failure is non-fatal — the selector
    // simply stays hidden and free-text anchoring is unaffected.
    useEffect(() => {
        let cancelled = false;
        setBlueprintsLoading(true);
        promptApi
            .getAll()
            .then((prompts) => {
                if (cancelled) return;
                setBlueprints(prompts.map((p) => ({id: p.id, name: p.name})));
            })
            .catch(() => {
                if (cancelled) return;
                setBlueprints([]);
            })
            .finally(() => {
                if (cancelled) return;
                setBlueprintsLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    // Abort any in-flight scoring request when this page unmounts OR the
    // active market changes — otherwise resolution of the previous market's
    // request would race the [market] reset effect below and overwrite the
    // freshly-blank fitMap/fitStats with stale scores (bd deepcontext-9h7).
    useEffect(
        () => () => {
            scoreAbortRef.current?.abort();
        },
        [market],
    );

    // Reset the anchor state whenever the market changes.
    useEffect(() => {
        setAnchor('');
        setSelectedBlueprintId(null);
        setFitMap(null);
        setFitStats(null);
    }, [market]);

    const topStories = payload?.top ?? [];
    const restStories = payload?.rest ?? [];

    // Shared scoring driver for both anchor sources (free-text + blueprint,
    // deepcontext-iasew). Cancels any prior request, runs `score()`, and maps
    // the result into the fitMap/fitStats the bubble graph reads. The two
    // public handlers differ only in which scoring request they fire and which
    // anchor-source state they set.
    const runScoring = useCallback(
        async (score: (signal: AbortSignal) => Promise<StoryFitScore[]>) => {
            if (!market) return;
            // Cancel any prior scoring request so the next answer wins.
            scoreAbortRef.current?.abort();
            const controller = new AbortController();
            scoreAbortRef.current = controller;
            setScoring(true);
            setScoreError(null);
            try {
                const scores = await score(controller.signal);
                if (controller.signal.aborted) return;
                const map = fitMapFromScores(scores);
                setFitMap(map);
                // Story fit scores don't carry a matched-URL count (there is
                // no per-story member breakdown in the score payload) — the
                // stat line reports how many live stories were scored.
                const segmentCount = scores.length;
                const avgFit =
                    scores.length > 0
                        ? scores.reduce((s, x) => s + x.fit_score, 0) / scores.length
                        : 0;
                setFitStats({segmentCount, avgFit});
            } catch (err: unknown) {
                if (isAbortError(err) || controller.signal.aborted) return;
                setScoreError(extractApiError(err, 'Failed to score anchor.'));
                setFitMap(null);
                setFitStats(null);
            } finally {
                if (!controller.signal.aborted) setScoring(false);
            }
        },
        [market],
    );

    const handleAnchor = useCallback(
        async (brief: string) => {
            if (!brief.trim()) return;
            if (!market) return;
            // A free-text anchor supersedes any blueprint anchor.
            setSelectedBlueprintId(null);
            setAnchor(brief);
            await runScoring(async (signal) => {
                const result = await clustersApi.scoreBrief(brief, market, {signal});
                return result.scores;
            });
        },
        [market, runScoring],
    );

    const handleAnchorBlueprint = useCallback(
        async (blueprintId: string) => {
            if (!blueprintId) return;
            if (!market) return;
            const bp = blueprints.find((b) => b.id === blueprintId);
            setSelectedBlueprintId(blueprintId);
            // Surface the blueprint name as the anchor chip label.
            setAnchor(bp ? bp.name : blueprintId);
            await runScoring(async (signal) => {
                const result = await clustersApi.scoreBlueprint(blueprintId, market, {
                    signal,
                });
                return result.scores;
            });
        },
        [market, blueprints, runScoring],
    );

    const handleClearAnchor = useCallback(() => {
        setAnchor('');
        setSelectedBlueprintId(null);
        setFitMap(null);
        setFitStats(null);
        setScoreError(null);
    }, []);

    // bd deepcontext-d68.17: once the market has resolved, seed the anchor
    // from `location.state.briefText` (captured into a ref on mount) and
    // immediately fire the same scoring flow the AnchorBar submit path uses.
    // After consuming, clear router state via `navigate('.', {replace: true})`
    // so a refresh lands on an empty overview rather than re-seeding.
    useEffect(() => {
        if (!market) return;
        const seed = seedFromStateRef.current;
        if (!seed) return;
        // Consume immediately so this never runs twice (e.g. on market change).
        seedFromStateRef.current = null;
        void handleAnchor(seed.briefText);
        // Clear the consumed router state. Pass `replace: true` so the
        // history entry stays put (no extra back-stack noise).
        navigate('.', {replace: true, state: null});
        // We intentionally depend only on `market`: seed consumption is
        // idempotent via the ref, and `handleAnchor`/`navigate` identities
        // can churn between renders.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [market]);

    return (
        <div
            data-testid="clusters-page"
            className="flex flex-col h-full min-h-0 gap-3"
        >
            {/* bd deepcontext-r4xu: search bar flex-grows left, market
                selector + URL-count pill hug the right edge — one row, equal
                height, matching radius. `MarketSelector`/`UrlCountBadge` need
                a real `ClustersMarketProvider`; skip them in the (test-only)
                mode where `market` is passed as a prop without one.

                deepcontext-l8re: `AnchorBar` now renders its own pill +
                toggle + actions as top-level siblings of this row (a
                Fragment) instead of a single wrapped block — the toggle sits
                next to the pill and the gradient Anchor action is pinned to
                the far right via `order-last`, matching the locked mockup's
                Blueprints-toolbar anatomy.

                deepcontext-mdp8: `-mt-6` pulls the row up by exactly
                AppLayout's shared header-row `mb-6` (AppLayout.tsx:206-218),
                so this row's Market pill lands on the same baseline as the
                Market pill TrendingPage/TopMentionsPage portal into that
                header row — same Tailwind token on both sides so they stay
                coupled.

                deepcontext-mfoi: the captioned `MarketSelector` now leads the
                row (locked mockup option A — "Market leads the same row"),
                with `AnchorBar` shrinking to follow and `UrlCountBadge`
                staying after it (the gradient Anchor action still pins to
                the far right via its own `order-last`). `items-end` (was
                `items-stretch`) bottom-aligns every pill to one baseline now
                that the caption sits above the MarketSelector trigger and
                makes it taller than the row's other 42px controls. */}
            <div
                data-testid="clusters-controls-row"
                className="-mt-6 flex items-end gap-2.5 flex-shrink-0"
            >
                {ctx && <MarketSelector captioned />}
                <AnchorBar
                    value={anchor}
                    onAnchor={handleAnchor}
                    onClear={handleClearAnchor}
                    segmentCount={fitStats?.segmentCount}
                    segmentLabel="stories"
                    avgFit={fitStats?.avgFit}
                    marketLabel={marketLabel}
                    loading={scoring}
                    blueprints={blueprints}
                    blueprintsLoading={blueprintsLoading}
                    selectedBlueprintId={selectedBlueprintId}
                    onAnchorBlueprint={handleAnchorBlueprint}
                />
                {ctx && <UrlCountBadge />}
            </div>

            {scoreError && (
                <p
                    data-testid="clusters-score-error"
                    className="text-xs text-risk flex-shrink-0"
                >
                    {scoreError}
                </p>
            )}

            {/* bd deepcontext-r4xu: the Card/CardContent wrapper and the
                right-rail <aside> are gone — this is now the single bordered
                rounded chart container, spanning full width.

                deepcontext-v6qp: this frame carries no card styling of its
                own anymore — it's a plain flex layout wrapper. ClustersGraph
                already draws its own bordered gradient panel
                (getMapPanelStyle), so wrapping it in a second bordered
                bg-surface card doubled the frame and ate ~12px on every
                side. The loading/error/empty messages render inside a panel
                using the same getMapPanelStyle so every state still shows
                exactly one frame, aligned with the filter bar above it.

                deepcontext-rlvt: zoom/pan restored and the ranked table
                dropped — the bubble map is a flex-filled frame again (not a
                fixed 840px height) since there's no table underneath to make
                room for; the map reclaims the full width and height. */}
            <div
                data-testid="clusters-chart-frame"
                className="flex-1 min-h-0 flex flex-col"
            >
                {loading ? (
                    <div
                        data-testid="clusters-loading"
                        className="flex-1 min-h-0 flex items-center justify-center rounded-2xl px-4 py-12 text-sm text-ink-muted text-center"
                        style={getMapPanelStyle(palette.mode)}
                    >
                        Loading Universe…
                    </div>
                ) : loadError ? (
                    <div
                        data-testid="clusters-load-error"
                        className="flex-1 min-h-0 flex items-center justify-center rounded-2xl px-4 py-12 text-sm text-risk text-center"
                        style={getMapPanelStyle(palette.mode)}
                    >
                        {loadError}
                    </div>
                ) : topStories.length === 0 ? (
                    <div
                        data-testid="clusters-empty"
                        className="flex-1 min-h-0 flex items-center justify-center rounded-2xl px-4 py-12 text-sm text-ink-muted text-center"
                        style={getMapPanelStyle(palette.mode)}
                    >
                        No stories available for market {market ?? '—'}.
                    </div>
                ) : (
                    <ClustersGraph
                        top={topStories}
                        rest={restStories}
                        fitMap={fitMap ?? undefined}
                        anchorActive={fitMap != null}
                        loadDetail={(id) => clustersApi.getTopic(id)}
                    />
                )}
            </div>
        </div>
    );
};

export default ClustersPage;
